import { AppThunk } from "../../app/store";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  arrayUnion,
} from "firebase/firestore";
import { auth, firestore, getFileUrl, signOut } from "../../firebase";
import {
  next,
  resetGame,
  selectAnswerState,
  setGameDefinition,
  setGameOptions,
} from "./game-state.slice";
import { Game, GameEntity, MatchGameData } from "./games-overview.type";
import { resetGames, setGames } from "./games-overview.slice";
import { resetStorage, setUrl } from "./storage.slice";
import { Question } from "./game-state.type";
import { resetProfile, setProfile } from "./user-profile.slice";
import { UserProfile } from "./user-profile.type";

const shuffle = require("knuth-shuffle-seeded");

export const generateGame =
  (questionAmount: number, optionsPerQuestion: number): AppThunk =>
  (dispatch, getState) => {
    const allOptionIds = getState().gameState.options.allIds;
    if (
      allOptionIds.length < optionsPerQuestion ||
      allOptionIds.length < questionAmount
    ) {
      throw new Error("Not enough game options available to generate a game");
    }
    const correctOptions: string[] = shuffle([...allOptionIds]).slice(
      0,
      questionAmount
    );
    const questions: Question[] = correctOptions.map((correctOption) => {
      const otherAnswers = shuffle(
        allOptionIds.filter((value) => value !== correctOption)
      );
      return {
        options: shuffle([
          correctOption,
          ...otherAnswers.slice(0, optionsPerQuestion - 1),
        ]),
        correctOption: correctOption,
      };
    });
    dispatch(setGameDefinition({ questions }));
  };

const gameConverter = {
  toFirestore: (data: Game) => {
    return {
      title: data.title,
      imageUrl: data.imageUrl,
      icon: data.icon,
      dataRef: doc(firestore, `game-data/${data.gameDataId}`),
      beta: data.beta,
      category: data.category,
    };
  },
  fromFirestore: (snapshot: any) => {
    const data = snapshot.data() as GameEntity;
    return {
      title: data.title,
      imageUrl: data.imageUrl,
      icon: data.icon,
      gameDataId: data.dataRef.id,
      beta: data.beta,
      category: data.category,
    };
  },
};

export const loadGames = (): AppThunk => (dispatch, getState) => {
  if (getState().gameOverviewState.games.allIds.length === 0) {
    getDocs(
      collection(firestore, "games").withConverter<Game>(gameConverter)
    ).then((snapshot) => {
      const games = snapshot.docs
        .map((current) => ({ id: current.id, data: current.data() }))
        .sort((a, b) => a.data.title.localeCompare(b.data.title))
        .reduce((acc, current) => {
          acc[current.id] = current.data;
          return acc;
        }, {} as Record<string, Game>);
      dispatch(setGames(games));
    });
  }
};

export const loadGame =
  (gameId: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const game = getState().gameOverviewState.games.byId[gameId];
    let gameDataId: string;
    if (game) {
      gameDataId = game.gameDataId;
    } else {
      const gameRef = doc(firestore, `games/${gameId}`).withConverter<Game>(
        gameConverter
      );
      const retrievedGame = await getDoc(gameRef);
      if (retrievedGame.exists()) {
        gameDataId = retrievedGame.data().gameDataId;
      } else {
        throw new Error("Could not load game");
      }
    }
    const gameDataRef = doc(firestore, `game-data/${gameDataId}`);
    const retrievedGameData = await getDoc(gameDataRef);
    if (retrievedGameData.exists()) {
      const gameData = retrievedGameData.data() as MatchGameData;
      dispatch(setGameOptions({ id: gameId, options: gameData.optionIds }));
    } else {
      throw new Error("Game data not found");
    }
  };

export const retrieveUrl =
  (path: string): AppThunk =>
  (dispatch, getState) => {
    if (!getState().storageState[path]) {
      getFileUrl(path).then((url) => dispatch(setUrl({ path: path, url })));
    }
  };

export const playSound =
  (path: string, onend?: () => any, volume?: number): AppThunk<Promise<void>> =>
  (dispatch, getState) => {
    const resolvedUrl = getState().storageState[path];
    if (!resolvedUrl) {
      return getFileUrl(path).then((url) => {
        dispatch(setUrl({ path: path, url }));
        return play(url, onend, volume);
      });
    } else {
      return play(resolvedUrl, onend, volume);
    }
  };

export const play = (
  src: string,
  onend?: () => any,
  volume?: number
): Promise<void> => {
  const audio = new Audio(src);
  if (volume) {
    audio.volume = volume;
  }
  const playbackPromise = audio.play();
  if (onend) {
    audio.onended = () => onend();
  }
  return playbackPromise;
};

export const incrementQuestion = (): AppThunk => (dispatch, getState) => {
  dispatch(next());
  if (getState().gameState.play?.finished) {
    dispatch(saveScore());
  }
};

export const saveScore =
  (): AppThunk<Promise<void>> => (dispatch, getState) => {
    const state = getState();
    const userId = auth.currentUser?.uid;
    const answerState = selectAnswerState(state);
    const gameId = state.gameState.gameId;

    if (userId && answerState && gameId && state.gameState.play?.finished) {
      const userProfileRef = doc(firestore, "users", userId);
      return setDoc(
        userProfileRef,
        {
          scores: {
            [gameId]: arrayUnion({
              date: Date.now(),
              correct: answerState.correct,
              total: answerState.total,
            }),
          },
        },
        { merge: true }
      );
    } else {
      throw new Error("User must be logged in");
    }
  };

export const loadProfile = (): AppThunk => async (dispatch) => {
  const userId = auth.currentUser?.uid;
  if (userId) {
    const userProfileRef = doc(firestore, "users", userId);
    const userProfileSnapshot = await getDoc(userProfileRef);
    if (userProfileSnapshot.exists()) {
      const userProfile = userProfileSnapshot.data() as UserProfile;
      dispatch(setProfile(userProfile));
    }
  }
};

export const logOut = (): AppThunk => (dispatch) => {
  dispatch(resetProfile());
  dispatch(resetGames());
  dispatch(resetGame());
  dispatch(resetStorage());
  return signOut();
};
